import { useState, useMemo, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Package,
  AlertTriangle,
  TrendingUp,
  Clock,
  CheckCircle2,
  ArrowRight,
  Plus,
  FileText,
  BarChart3,
  Zap,
  Brain,
  Warehouse,
  Eye,
  MapPin,
  Activity,
  Loader2,
  Monitor,
  TestTube2,
  Settings,
  RefreshCw,
  Calendar,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { HelpButton } from "@/components/help-button";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { useNavigation } from "@/components/layout/app-layout";
import { getStockItems } from "@/services/stockDataService";
import { mockWarehouses, mockLocations } from "@/data/mockWarehousesLocations";
import { mockStockTakes } from "@/data/mockStockTakes";
import { useScreenMode } from "@/providers/ModeProvider";
import { useDashboardData } from "@/hooks/use-dashboard-data";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

// Helper function to map colors to variants
const mapColorToVariant = (color: string): "success" | "warning" | "error" | "info" => {
  if (color.includes("red") || color.includes("rose") || color.includes("crimson")) {
    return "error";
  }
  if (color.includes("yellow") || color.includes("amber") || color.includes("orange")) {
    return "warning";
  }
  if (color.includes("green") || color.includes("emerald")) {
    return "success";
  }
  // All other colors (blue, purple, indigo, cyan, teal, etc.) map to info
  return "info";
};

// Types
interface QuickStat {
  label: string;
  value: string | number;
  icon: React.ElementType;
  trend?: string;
  trendUp?: boolean;
  color: string;
}

interface ActionItem {
  id: string;
  title: string;
  description: string;
  priority: "HIGH" | "MEDIUM" | "LOW";
  category: string;
  action: string;
  actionLink: string;
  filterParams?: any; // Parameters to pass to the target page
}

interface StockAlert {
  id: string;
  stockCode: string;
  description: string;
  currentStock: number;
  reorderPoint: number;
  daysUntilStockout: number;
  status: "CRITICAL" | "WARNING";
}

interface RecentActivity {
  id: string;
  type: "movement" | "order" | "receive" | "adjustment";
  description: string;
  user: string;
  timestamp: string;
  icon: React.ElementType;
}

interface DashboardSettings {
  // KPI Cards
  kpiCards: {
    totalStockValue: boolean;
    activeItems: boolean;
    lowStockAlerts: boolean;
    stockAccuracy: boolean;
    pendingOrders: boolean;
    warehouseUtilization: boolean;
    reorderPoints: boolean;
    expiringItems: boolean;
  };
  // Secondary Stats
  secondaryStats: {
    stockTakes: boolean;
    warehouses: boolean;
    categories: boolean;
  };
  // Quick Actions
  quickActions: {
    newStockTake: boolean;
    receiveStock: boolean;
    transferStock: boolean;
    adjustStock: boolean;
    viewReports: boolean;
    manageLocations: boolean;
  };
  // Action Items
  actionItems: boolean;
  // Stock Alerts
  stockAlerts: boolean;
  // Recent Activity
  recentActivity: boolean;
  // Stock By Type
  stockByType: boolean;
  // Top Value Items
  topValueItems: boolean;
  // System Capabilities
  capabilities: boolean;
}

const DEFAULT_SETTINGS: DashboardSettings = {
  kpiCards: {
    totalStockValue: true,
    activeItems: true,
    lowStockAlerts: true,
    stockAccuracy: true,
    pendingOrders: false,
    warehouseUtilization: false,
    reorderPoints: false,
    expiringItems: false,
  },
  secondaryStats: {
    stockTakes: false,
    warehouses: false,
    categories: false,
  },
  quickActions: {
    newStockTake: true,
    receiveStock: true,
    transferStock: true,
    adjustStock: true,
    viewReports: true,
    manageLocations: true,
  },
  actionItems: true,
  stockAlerts: true,
  recentActivity: true,
  stockByType: true,
  topValueItems: true,
  capabilities: true,
};

export function DashboardPage() {
  const { navigateTo } = useNavigation();
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("dashboard");
  const [activeTab, setActiveTab] = useState("actions");
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settings, setSettings] = useState<DashboardSettings>(DEFAULT_SETTINGS);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem("dashboardSettings");
    if (savedSettings) {
      try {
        setSettings(JSON.parse(savedSettings));
      } catch (e) {
        console.error("Failed to parse dashboard settings:", e);
      }
    }
  }, []);

  // Save settings to localStorage
  const saveSettings = (newSettings: DashboardSettings) => {
    setSettings(newSettings);
    localStorage.setItem("dashboardSettings", JSON.stringify(newSettings));
  };

  const toggleKpiCard = (key: keyof DashboardSettings["kpiCards"]) => {
    const newSettings = {
      ...settings,
      kpiCards: { ...settings.kpiCards, [key]: !settings.kpiCards[key] },
    };
    saveSettings(newSettings);
  };

  const toggleSecondaryStat = (key: keyof DashboardSettings["secondaryStats"]) => {
    const newSettings = {
      ...settings,
      secondaryStats: { ...settings.secondaryStats, [key]: !settings.secondaryStats[key] },
    };
    saveSettings(newSettings);
  };

  const toggleQuickAction = (key: keyof DashboardSettings["quickActions"]) => {
    const newSettings = {
      ...settings,
      quickActions: { ...settings.quickActions, [key]: !settings.quickActions[key] },
    };
    saveSettings(newSettings);
  };

  const toggleSimpleSetting = (
    key: "actionItems" | "stockAlerts" | "recentActivity" | "stockByType" | "topValueItems" | "capabilities",
  ) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    saveSettings(newSettings);
  };

  const resetSettings = () => {
    saveSettings(DEFAULT_SETTINGS);
  };

  // Mode context for demo/live toggle (screen-specific)
  const { setMode, isDemo, isLive } = useScreenMode("dashboard");
  const isLiveMode = isLive;

  // Live data hook
  const { data: liveData, isLoading: isLiveLoading, fetchDashboardData } = useDashboardData();

  // Load mock data
  const mockStockItems = useMemo(() => getStockItems(), []);

  // Fetch live data when in live mode
  useEffect(() => {
    if (isLiveMode) {
      fetchDashboardData();
    }
  }, [isLiveMode, fetchDashboardData]);

  // Handle mode toggle
  const handleModeToggle = useCallback(
    (newIsLive: boolean) => {
      setMode(newIsLive ? "live" : "demo");
    },
    [setMode],
  );

  // Get current data source based on mode
  const stockItems = useMemo(() => {
    if (isLiveMode && liveData.stockItems.length > 0) {
      return liveData.stockItems.map((item) => ({
        code: item.stockCode,
        description: item.description,
        category: item.category || "Uncategorized",
        warehouseCode: item.warehouseCode || "",
        locationCode: item.locationCode || "",
        physicalQuantity: item.physicalQuantity,
        unitPrice: item.unitPrice,
        value: item.totalValue,
        inactive: item.inactive,
        lastMovementDate: item.lastMovementDate ? new Date(item.lastMovementDate) : new Date(),
        reorderPoint: item.reorderPoint,
        stockTurnRate: item.stockTurnRate || 0,
        abcClassification: (item.aBCClassification as "A" | "B" | "C") || "C",
        allocatedQuantity: item.allocatedQuantity,
        quantityDueIn: item.quantityDueIn,
        minQuantity: item.minQuantity,
        maxQuantity: item.maxQuantity,
        reorderQuantity: item.reorderQuantity,
        lastCostPrice: item.lastCostPrice,
        averageCost: item.averageCost,
        barcode: "",
      }));
    }
    return mockStockItems;
  }, [isLiveMode, liveData.stockItems, mockStockItems]);

  const warehouses = useMemo(() => {
    if (isLiveMode && liveData.warehouses.length > 0) {
      return liveData.warehouses.map((w) => ({
        code: w.warehouseCode,
        name: w.warehouseName,
        address: w.address || "",
        type: w.warehouseType as "Main" | "Satellite" | "Retail" | "Storage" | "Distribution",
        totalCapacity: w.totalCapacity || undefined,
        currentUtilization: w.currentUtilization || 0,
        active: w.active,
        defaultReceiveLocation: w.defaultReceiveLocation || undefined,
        defaultPickLocation: w.defaultPickLocation || undefined,
      }));
    }
    return mockWarehouses;
  }, [isLiveMode, liveData.warehouses]);

  const locations = useMemo(() => {
    if (isLiveMode && liveData.locations.length > 0) {
      return liveData.locations.map((l) => ({
        locationCode: l.locationCode,
        warehouseCode: l.warehouseCode,
        aisle: l.aisle || undefined,
        rack: l.rack || undefined,
        shelf: l.shelf || undefined,
        bin: l.bin || undefined,
        locationType: l.locationType as "Storage" | "Picking" | "Receiving" | "Staging" | "Shipping" | "Quarantine",
        capacity: l.capacity || undefined,
        currentStock: l.currentStock,
        utilizationPercent: l.utilizationPercent,
        pickingSequence: l.pickingSequence || undefined,
        active: l.active,
      }));
    }
    return mockLocations;
  }, [isLiveMode, liveData.locations]);

  const stockTakes = useMemo(() => {
    if (isLiveMode && liveData.stockTakes.length > 0) {
      return liveData.stockTakes.map((st) => ({
        id: String(st.stockTakeID),
        reference: st.stockTakeReference,
        description: st.description,
        type: st.stockTakeType as "Full Stock Take" | "Partial Stock Take" | "Location Audit" | "Cycle Count",
        warehouse: st.warehouse,
        warehouseCode: st.warehouseCode,
        status: st.status as
          | "Draft"
          | "Started"
          | "In Progress"
          | "Review"
          | "Complete"
          | "Completed"
          | "Scheduled"
          | "Paused"
          | "Cancelled",
        createdBy: st.createdBy,
        createdAt: new Date(st.createdAt),
        assignedTo: st.assignedTo || "Unassigned",
        startedAt: st.startedAt ? new Date(st.startedAt) : undefined,
        completedAt: st.completedAt ? new Date(st.completedAt) : undefined,
        items: [],
        totalItems: st.totalItems,
        countedItems: st.countedItems,
        varianceCount: st.varianceCount,
        notes: st.notes || undefined,
      }));
    }
    return mockStockTakes;
  }, [isLiveMode, liveData.stockTakes]);

  // Calculate statistics from current data source
  const stats = useMemo(() => {
    const activeItems = stockItems.filter((item) => !item.inactive);
    const totalValue = activeItems.reduce((sum, item) => sum + item.physicalQuantity * item.unitPrice, 0);
    const outOfStock = activeItems.filter((item) => item.physicalQuantity === 0);
    const lowStock = activeItems.filter(
      (item) => item.physicalQuantity > 0 && item.physicalQuantity <= item.reorderPoint,
    );
    const activeWarehouses = warehouses.filter((w) => w.active);
    const activeLocations = locations.filter((l) => l.active);
    const inProgressStockTakes = stockTakes.filter((st) => st.status === "In Progress" || st.status === "Started");
    const scheduledStockTakes = stockTakes.filter((st) => st.status === "Scheduled");

    const totalPhysicalStock = activeItems.reduce((sum, item) => sum + item.physicalQuantity, 0);

    return {
      totalValue,
      activeItemsCount: activeItems.length,
      outOfStockCount: outOfStock.length,
      lowStockCount: lowStock.length,
      warehouseCount: activeWarehouses.length,
      locationCount: activeLocations.length,
      inProgressStockTakesCount: inProgressStockTakes.length,
      scheduledStockTakesCount: scheduledStockTakes.length,
      outOfStockItems: outOfStock,
      lowStockItems: lowStock,
      totalPhysicalStock,
    };
  }, [stockItems, warehouses, locations, stockTakes]);

  // Quick Stats Data (now using real data)
  const allQuickStats: Array<QuickStat & { id: keyof DashboardSettings["kpiCards"] }> = [
    {
      id: "totalStockValue",
      label: "Total Stock Value",
      value: `£${(stats.totalValue / 1000000).toFixed(2)}M`,
      icon: TrendingUp,
      trend: `${stats.activeItemsCount} active items`,
      trendUp: true,
      color: "bg-green-500",
    },
    {
      id: "activeItems",
      label: "Active Items",
      value: stats.activeItemsCount.toLocaleString(),
      icon: Package,
      trend: `${stats.warehouseCount} warehouses`,
      trendUp: true,
      color: "bg-blue-500",
    },
    {
      id: "lowStockAlerts",
      label: "Low Stock Alerts",
      value: stats.lowStockCount,
      icon: AlertTriangle,
      trend: "Below reorder point",
      trendUp: false,
      color: "bg-yellow-500",
    },
    {
      id: "stockAccuracy",
      label: "Physical Stock",
      value: stats.totalPhysicalStock.toLocaleString(),
      icon: Package,
      trend: "Total units in stock",
      trendUp: true,
      color: "bg-emerald-500",
    },
    {
      id: "pendingOrders",
      label: "Pending Orders",
      value: "24",
      icon: Clock,
      trend: "Awaiting processing",
      trendUp: false,
      color: "bg-orange-500",
    },
    {
      id: "warehouseUtilization",
      label: "Warehouse Utilisation",
      value: `${Math.round(
        warehouses.reduce((sum, w) => sum + (w.active ? w.currentUtilization : 0), 0) /
          warehouses.filter((w) => w.active).length,
      )}%`,
      icon: Warehouse,
      trend: "Average across all",
      trendUp: true,
      color: "bg-indigo-500",
    },
    {
      id: "reorderPoints",
      label: "Items at Reorder Point",
      value: stats.lowStockCount,
      icon: RefreshCw,
      trend: "Need reordering",
      trendUp: false,
      color: "bg-purple-500",
    },
    {
      id: "expiringItems",
      label: "Expiring Soon",
      value: "12",
      icon: Calendar,
      trend: "Next 30 days",
      trendUp: false,
      color: "bg-red-500",
    },
  ];

  // Filter KPI cards based on settings
  const quickStats = allQuickStats.filter((stat) => settings.kpiCards[stat.id]);

  // Action Items Data with filter parameters (using real data)
  const actionItems: ActionItem[] = useMemo(() => {
    const items: ActionItem[] = [];

    // Out of Stock Items
    if (stats.outOfStockCount > 0) {
      items.push({
        id: "1",
        title: `${stats.outOfStockCount} Items Out of Stock`,
        description: "Critical items need immediate reordering to prevent stockouts",
        priority: "HIGH",
        category: "Stock Management",
        action: "View Items",
        actionLink: "stock-items",
        filterParams: {
          filters: [
            {
              id: "filter-outofstock",
              field: "physicalQuantity",
              operator: "equals" as const,
              value: 0,
              value2: null,
            },
          ],
          logic: [],
        },
      });
    }

    // Low Stock Items
    if (stats.lowStockCount > 0) {
      items.push({
        id: "3",
        title: `${stats.lowStockCount} Items Below Reorder Point`,
        description: "Stock levels approaching minimum thresholds",
        priority: stats.lowStockCount > 20 ? "HIGH" : "MEDIUM",
        category: "Stock Management",
        action: "Review Stock",
        actionLink: "stock-items",
        filterParams: {
          filters: [
            {
              id: "filter-belowreorder",
              field: "isBelowReorderPoint",
              operator: "isYes" as const,
              value: null,
              value2: null,
            },
          ],
          logic: [],
        },
      });
    }

    // In Progress Stock Takes
    if (stats.inProgressStockTakesCount > 0) {
      items.push({
        id: "4",
        title: `${stats.inProgressStockTakesCount} Stock Takes In Progress`,
        description: "Active stock counts requiring attention",
        priority: "MEDIUM",
        category: "Stock Takes",
        action: "View Stock Takes",
        actionLink: "stock-takes",
        filterParams: {
          statusFilter: "in-progress",
        },
      });
    }

    // Scheduled Stock Takes
    if (stats.scheduledStockTakesCount > 0) {
      items.push({
        id: "5",
        title: `${stats.scheduledStockTakesCount} Scheduled Stock Takes`,
        description: "Upcoming stock counts to prepare for",
        priority: "MEDIUM",
        category: "Stock Takes",
        action: "View Schedule",
        actionLink: "stock-takes",
        filterParams: {
          statusFilter: "scheduled",
        },
      });
    }

    // Warehouse Utilisation
    const highUtilizationWarehouses = warehouses.filter((w) => w.active && w.currentUtilization > 80);
    if (highUtilizationWarehouses.length > 0) {
      items.push({
        id: "6",
        title: `${highUtilizationWarehouses.length} Warehouses Over 80% Capacity`,
        description: `${highUtilizationWarehouses.map((w) => w.name).join(", ")} need attention`,
        priority: "MEDIUM",
        category: "Warehouse Management",
        action: "View Warehouses",
        actionLink: "warehouses-locations",
      });
    }

    // ABC Classification
    const classAItems = stockItems.filter((item) => !item.inactive && item.abcClassification === "A");
    if (classAItems.length > 0) {
      items.push({
        id: "7",
        title: `${classAItems.length} High-Value Items (Class A)`,
        description: "Monitor your most valuable inventory items",
        priority: "LOW",
        category: "Analytics",
        action: "View ABC Analysis",
        actionLink: "abc-classification",
      });
    }

    // AI Forecasting
    items.push({
      id: "8",
      title: "AI Demand Forecasting Available",
      description: "Optimise inventory with predictive analytics",
      priority: "LOW",
      category: "Analytics",
      action: "View Forecast",
      actionLink: "ai-forecasting",
    });

    return items;
  }, [stats, warehouses, stockItems]);

  // Stock Alerts Data (using real data)
  const stockAlerts: StockAlert[] = useMemo(() => {
    const alerts: StockAlert[] = [];

    // Combine out of stock and low stock items
    const criticalItems = [...stats.outOfStockItems, ...stats.lowStockItems]
      .sort((a, b) => {
        // Sort by severity: out of stock first, then by how far below reorder point
        if (a.physicalQuantity === 0 && b.physicalQuantity > 0) return -1;
        if (a.physicalQuantity > 0 && b.physicalQuantity === 0) return 1;
        return a.physicalQuantity - b.physicalQuantity;
      })
      .slice(0, 10); // Show top 10 alerts

    criticalItems.forEach((item, index) => {
      // Estimate days until stockout based on stock turn rate
      let daysUntilStockout = 0;
      if (item.physicalQuantity > 0 && item.stockTurnRate) {
        // If stock turn rate is 12 (monthly), then daily usage is totalValue/365
        const estimatedDailyUsage = (item.stockTurnRate / 365) * item.physicalQuantity;
        daysUntilStockout = estimatedDailyUsage > 0 ? Math.floor(item.physicalQuantity / estimatedDailyUsage) : 30;
      }

      alerts.push({
        id: String(index + 1),
        stockCode: item.code,
        description: item.description,
        currentStock: item.physicalQuantity,
        reorderPoint: item.reorderPoint,
        daysUntilStockout,
        status:
          item.physicalQuantity === 0
            ? "CRITICAL"
            : item.physicalQuantity <= item.reorderPoint * 0.5
            ? "CRITICAL"
            : "WARNING",
      });
    });

    return alerts;
  }, [stats]);

  // Recent Activity Data (using real data from stock takes and items)
  const recentActivity: RecentActivity[] = useMemo(() => {
    const activities: RecentActivity[] = [];

    // Add recent stock take activities
    const recentStockTakes = [...stockTakes]
      .sort((a, b) => {
        const dateA = a.startedAt || a.createdAt;
        const dateB = b.startedAt || b.createdAt;
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, 3);

    recentStockTakes.forEach((st, index) => {
      const date = st.startedAt || st.createdAt;
      const hoursAgo = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60));
      const timeStr =
        hoursAgo < 1
          ? "Less than 1 hour ago"
          : hoursAgo === 1
          ? "1 hour ago"
          : hoursAgo < 24
          ? `${hoursAgo} hours ago`
          : `${Math.floor(hoursAgo / 24)} days ago`;

      activities.push({
        id: `st-${index}`,
        type: "adjustment",
        description: `Stock Take ${st.reference}: ${st.status} - ${st.warehouse} (${st.countedItems}/${st.totalItems} counted)`,
        user: st.assignedTo,
        timestamp: timeStr,
        icon: FileText,
      });
    });

    // Add recent stock movements based on item last movement dates
    const recentlyMovedItems = [...stockItems]
      .filter((item) => item.lastMovementDate)
      .sort((a, b) => {
        const dateA = a.lastMovementDate?.getTime() || 0;
        const dateB = b.lastMovementDate?.getTime() || 0;
        return dateB - dateA;
      })
      .slice(0, 5);

    recentlyMovedItems.forEach((item, index) => {
      if (!item.lastMovementDate) return;

      const hoursAgo = Math.floor((Date.now() - item.lastMovementDate.getTime()) / (1000 * 60 * 60));
      const timeStr =
        hoursAgo < 1
          ? "Less than 1 hour ago"
          : hoursAgo === 1
          ? "1 hour ago"
          : hoursAgo < 24
          ? `${hoursAgo} hours ago`
          : `${Math.floor(hoursAgo / 24)} days ago`;

      const warehouse = warehouses.find((w) => w.code === item.warehouseCode);

      activities.push({
        id: `mov-${index}`,
        type: "movement",
        description: `Stock movement: ${item.code} - ${item.description} (${item.physicalQuantity} units in ${
          warehouse?.name || item.warehouseCode
        })`,
        user: "System",
        timestamp: timeStr,
        icon: ArrowRight,
      });
    });

    // Sort all activities by most recent
    return activities
      .sort((a, b) => {
        // Simple sort by extracting numbers from timestamp strings
        const getMinutes = (ts: string) => {
          if (ts.includes("Less than")) return 0;
          if (ts.includes("hour ago") && !ts.includes("hours")) return 60;
          if (ts.includes("hours ago")) return parseInt(ts) * 60;
          if (ts.includes("days ago")) return parseInt(ts) * 24 * 60;
          return 0;
        };
        return getMinutes(a.timestamp) - getMinutes(b.timestamp);
      })
      .slice(0, 8); // Show top 8 activities
  }, [stockTakes, stockItems, warehouses]);

  // Get priority badge color
  const getPriorityColor = (priority: ActionItem["priority"]) => {
    switch (priority) {
      case "HIGH":
        return "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300";
      case "MEDIUM":
        return "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300";
      case "LOW":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
    }
  };

  // Get alert status color
  const getAlertColor = (status: StockAlert["status"]) => {
    return status === "CRITICAL"
      ? "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
      : "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Home</h1>
            {isLiveMode ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                Live Data
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                Demo Mode
              </Badge>
            )}
            {isLiveLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <div className="flex items-center gap-2">
              <TestTube2 className={cn("h-4 w-4", isDemo ? "text-yellow-600" : "text-muted-foreground")} />
              <Switch
                id="dashboard-mode"
                checked={isLiveMode}
                onCheckedChange={handleModeToggle}
                aria-label="Toggle between demo and live mode"
              />
              <Monitor className={cn("h-4 w-4", isLiveMode ? "text-green-600" : "text-muted-foreground")} />
              <Label htmlFor="dashboard-mode" className="text-sm text-muted-foreground cursor-pointer">
                {isLiveMode ? "Live" : "Demo"}
              </Label>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="default" onClick={() => setIsSettingsOpen(true)} className="gap-2">
              <Settings className="h-4 w-4" />
              Edit Dashboard
            </Button>
            <HelpButton onClick={toggleHelp} variant="outline" size="default" showLabel />
          </div>
        </div>
        <p className="text-muted-foreground mt-2 font-normal">
          Your command center for intelligent stock management and actionable insights.
        </p>
      </div>

      {/* Quick Actions */}
      {(() => {
        const allQuickActions = [
          { id: "newStockTake" as const, icon: FileText, label: "New Stock Take", route: "stock-takes" },
          { id: "receiveStock" as const, icon: Package, label: "Receive Stock", route: "receive-goods" },
          { id: "transferStock" as const, icon: ArrowRight, label: "Transfer Stock", route: "add-movement" },
          { id: "adjustStock" as const, icon: Plus, label: "Adjust Stock", route: "stock-items" },
          { id: "manageLocations" as const, icon: MapPin, label: "Manage Locations", route: "warehouses-locations" },
        ];
        const visibleActions = allQuickActions.filter((action) => settings.quickActions[action.id]);

        if (visibleActions.length === 0) return null;

        return (
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>Common tasks for efficient inventory management</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {visibleActions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <Button
                      key={action.id}
                      variant="outline"
                      className="h-auto flex-col gap-3 p-6"
                      onClick={() => navigateTo(action.route)}
                    >
                      <Icon className="h-6 w-6" />
                      <span className="text-sm font-medium">{action.label}</span>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Quick Stats */}
      {quickStats.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickStats.map((stat) => (
            <StaticMetricCardAlt
              key={stat.id}
              title={stat.label}
              value={typeof stat.value === "number" ? stat.value.toLocaleString() : stat.value.toString()}
              change={stat.trendUp ? "↑" : "↓"}
              changeLabel={stat.trend || ""}
              icon={stat.icon as any}
              variant={mapColorToVariant(stat.color)}
            />
          ))}
        </div>
      )}

      {/* Secondary Stats - Stock Takes & Locations */}
      {(settings.secondaryStats.stockTakes ||
        settings.secondaryStats.warehouses ||
        settings.secondaryStats.categories) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {settings.secondaryStats.stockTakes && (
            <StaticMetricCardAlt
              title="Active Stock Takes"
              value={stats.inProgressStockTakesCount.toString()}
              change="→"
              changeLabel={`${stats.scheduledStockTakesCount} scheduled`}
              icon={Activity}
              variant="info"
            />
          )}

          {settings.secondaryStats.warehouses && (
            <StaticMetricCardAlt
              title="Warehouses"
              value={stats.warehouseCount.toString()}
              change="→"
              changeLabel={`${stats.locationCount} locations`}
              icon={Warehouse}
              variant="info"
            />
          )}

          {settings.secondaryStats.categories && (
            <StaticMetricCardAlt
              title="Categories"
              value={new Set(stockItems.filter((i) => !i.inactive).map((i) => i.category)).size.toString()}
              change="→"
              changeLabel="Product categories"
              icon={Package}
              variant="info"
            />
          )}
        </div>
      )}

      {/* Main Content Tabs */}
      {(settings.actionItems || settings.stockAlerts || settings.recentActivity) && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            {settings.actionItems && (
              <TabsTrigger value="actions" className="gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Action Items
                <Badge variant="secondary" className="ml-1">
                  {actionItems.filter((item) => item.priority === "HIGH").length}
                </Badge>
              </TabsTrigger>
            )}
            {settings.stockAlerts && (
              <TabsTrigger value="alerts" className="gap-2">
                <AlertTriangle className="h-4 w-4" />
                Stock Alerts
                <Badge variant="secondary" className="ml-1">
                  {stockAlerts.filter((alert) => alert.status === "CRITICAL").length}
                </Badge>
              </TabsTrigger>
            )}
            {settings.recentActivity && (
              <TabsTrigger value="activity" className="gap-2">
                <Clock className="h-4 w-4" />
                Recent Activity
              </TabsTrigger>
            )}
          </TabsList>

          {/* Action Items Tab */}
          {settings.actionItems && (
            <TabsContent value="actions" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Prioritized Action Items</CardTitle>
                  <CardDescription>Tasks requiring your attention, sorted by priority</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                    {actionItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{item.title}</h4>
                            <Badge className={getPriorityColor(item.priority)} variant="secondary">
                              {item.priority}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {item.category}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{item.description}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="ml-4"
                          onClick={() => navigateTo(item.actionLink, item.filterParams)}
                        >
                          {item.action}
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Stock Alerts Tab */}
          {settings.stockAlerts && (
            <TabsContent value="alerts" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Critical Stock Alerts</CardTitle>
                  <CardDescription>Items requiring immediate attention to prevent stockouts</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                    {stockAlerts.map((alert) => (
                      <div
                        key={alert.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold font-mono">{alert.stockCode}</h4>
                            <Badge className={getAlertColor(alert.status)} variant="secondary">
                              {alert.status}
                            </Badge>
                          </div>
                          <p className="text-sm">{alert.description}</p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>Current: {alert.currentStock} units</span>
                            <span>Reorder Point: {alert.reorderPoint} units</span>
                            <span className="font-semibold text-red-600">
                              {alert.daysUntilStockout === 0
                                ? "OUT OF STOCK"
                                : `${alert.daysUntilStockout} days until stockout`}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4 flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Navigate to stock items page with this specific item filtered
                              navigateTo("stock-items", {
                                filters: [
                                  {
                                    id: `filter-${Date.now()}`,
                                    field: "stockCode",
                                    operator: "equals",
                                    value: alert.stockCode,
                                    value2: null,
                                  },
                                ],
                                logic: [],
                              });
                            }}
                          />
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold font-mono">{alert.stockCode}</h4>
                              <Badge className={getAlertColor(alert.status)} variant="secondary">
                                {alert.status}
                              </Badge>
                            </div>
                            <p className="text-sm">{alert.description}</p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span>Current: {alert.currentStock} units</span>
                              <span>Reorder Point: {alert.reorderPoint} units</span>
                              <span className="font-semibold text-red-600">
                                {alert.daysUntilStockout === 0
                                  ? "OUT OF STOCK"
                                  : `${alert.daysUntilStockout} days until stockout`}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4 flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // Navigate to stock items page with this specific item filtered
                                navigateTo("stock-items", {
                                  filters: [
                                    {
                                      id: `filter-${Date.now()}`,
                                      field: "stockCode",
                                      operator: "equals",
                                      value: alert.stockCode,
                                      value2: null,
                                    },
                                  ],
                                  logic: [],
                                });
                              }}
                            >
                              View Item
                              <Eye className="h-4 w-4 ml-2" />
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              disabled
                              className="relative"
                              title="Purchase Order creation will be available soon via MFE integration"
                            >
                              Create PO
                              <Plus className="h-4 w-4 ml-2" />
                              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                              </span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Recent Activity Tab */}
          {settings.recentActivity && (
            <TabsContent value="activity" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                  <CardDescription>Latest operations and changes in your inventory system</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                    {recentActivity.map((activity) => {
                      const Icon = activity.icon;
                      return (
                        <div
                          key={activity.id}
                          className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="p-2 rounded-lg bg-muted">
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 space-y-1">
                            <p className="text-sm font-medium">{activity.description}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{activity.user}</span>
                              <span>•</span>
                              <span>{activity.timestamp}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      )}

      {/* Stock By Type & Top Value Items */}
      {(settings.stockByType || settings.topValueItems) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {settings.stockByType && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Stock By Type
                </CardTitle>
                <CardDescription>Inventory breakdown by category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(() => {
                    // Calculate stock by category
                    const categoryStats = mockStockItems
                      .filter((item) => !item.inactive)
                      .reduce((acc, item) => {
                        const category = item.category || "Uncategorized";
                        if (!acc[category]) {
                          acc[category] = { count: 0, value: 0 };
                        }
                        acc[category].count += 1;
                        acc[category].value += item.physicalQuantity * item.unitPrice;
                        return acc;
                      }, {} as Record<string, { count: number; value: number }>);

                    // Sort by value and get top 5
                    const sortedCategories = Object.entries(categoryStats)
                      .sort(([, a], [, b]) => b.value - a.value)
                      .slice(0, 5);

                    return sortedCategories.map(([category, data]) => (
                      <div key={category} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">{category}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{data.count} items</p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold">
                            £{(data.value / 1000000).toFixed(2)}M
                          </div>
                          <div className="text-xs text-muted-foreground">Total Value</div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </CardContent>
            </Card>
          )}

          {settings.topValueItems && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Top Value Items
                </CardTitle>
                <CardDescription>Highest value inventory items</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {mockStockItems
                    .filter((item) => !item.inactive)
                    .map((item) => ({
                      ...item,
                      totalValue: item.physicalQuantity * item.unitPrice,
                    }))
                    .sort((a, b) => b.totalValue - a.totalValue)
                    .slice(0, 5)
                    .map((item) => (
                      <div key={item.code} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">{item.description}</h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            {item.code} • {item.physicalQuantity} units
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold">
                            £{(item.totalValue / 1000).toFixed(1)}K
                          </div>
                          <div className="text-xs text-muted-foreground">
                            £{item.unitPrice.toFixed(2)}/unit
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* System Capabilities */}
      {settings.capabilities && (
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-blue-600" />
              Stock Evo Capabilities
            </CardTitle>
            <CardDescription>Intelligent features to optimise your inventory management</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border">
                <Brain className="h-5 w-5 text-purple-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-sm">AI Demand Forecasting</h4>
                  <p className="text-xs text-muted-foreground mt-1">Predict future demand with machine learning</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border">
                <BarChart3 className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-sm">ABC Classification</h4>
                  <p className="text-xs text-muted-foreground mt-1">Prioritise inventory by value and importance</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border">
                <Warehouse className="h-5 w-5 text-orange-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-sm">Multi-Warehouse</h4>
                  <p className="text-xs text-muted-foreground mt-1">Manage stock across multiple locations</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dashboard Settings Dialog */}
      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Customise Dashboard
            </DialogTitle>
            <DialogDescription>
              Show or hide sections to personalize your dashboard view. Your preferences will be saved automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* KPI Cards Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-1">KPI Cards</h3>
                <p className="text-xs text-muted-foreground">Select which metrics to display</p>
              </div>
              <div className="grid grid-cols-2 gap-3 pl-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="totalStockValue"
                    checked={settings.kpiCards.totalStockValue}
                    onCheckedChange={() => toggleKpiCard("totalStockValue")}
                  />
                  <Label htmlFor="totalStockValue" className="text-sm cursor-pointer">
                    Total Stock Value
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="activeItems"
                    checked={settings.kpiCards.activeItems}
                    onCheckedChange={() => toggleKpiCard("activeItems")}
                  />
                  <Label htmlFor="activeItems" className="text-sm cursor-pointer">
                    Active Items
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="lowStockAlerts"
                    checked={settings.kpiCards.lowStockAlerts}
                    onCheckedChange={() => toggleKpiCard("lowStockAlerts")}
                  />
                  <Label htmlFor="lowStockAlerts" className="text-sm cursor-pointer">
                    Low Stock Alerts
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="stockAccuracy"
                    checked={settings.kpiCards.stockAccuracy}
                    onCheckedChange={() => toggleKpiCard("stockAccuracy")}
                  />
                  <Label htmlFor="stockAccuracy" className="text-sm cursor-pointer">
                    Stock Accuracy
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="pendingOrders"
                    checked={settings.kpiCards.pendingOrders}
                    onCheckedChange={() => toggleKpiCard("pendingOrders")}
                  />
                  <Label htmlFor="pendingOrders" className="text-sm cursor-pointer">
                    Pending Orders
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="warehouseUtilization"
                    checked={settings.kpiCards.warehouseUtilization}
                    onCheckedChange={() => toggleKpiCard("warehouseUtilization")}
                  />
                  <Label htmlFor="warehouseUtilization" className="text-sm cursor-pointer">
                    Warehouse Utilisation
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="reorderPoints"
                    checked={settings.kpiCards.reorderPoints}
                    onCheckedChange={() => toggleKpiCard("reorderPoints")}
                  />
                  <Label htmlFor="reorderPoints" className="text-sm cursor-pointer">
                    Reorder Points
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="expiringItems"
                    checked={settings.kpiCards.expiringItems}
                    onCheckedChange={() => toggleKpiCard("expiringItems")}
                  />
                  <Label htmlFor="expiringItems" className="text-sm cursor-pointer">
                    Expiring Items
                  </Label>
                </div>
              </div>
            </div>

            <Separator />

            {/* Secondary Stats Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-1">Secondary Statistics</h3>
                <p className="text-xs text-muted-foreground">Additional metrics</p>
              </div>
              <div className="grid grid-cols-2 gap-3 pl-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="stockTakes"
                    checked={settings.secondaryStats.stockTakes}
                    onCheckedChange={() => toggleSecondaryStat("stockTakes")}
                  />
                  <Label htmlFor="stockTakes" className="text-sm cursor-pointer">
                    Active Stock Takes
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="warehouses"
                    checked={settings.secondaryStats.warehouses}
                    onCheckedChange={() => toggleSecondaryStat("warehouses")}
                  />
                  <Label htmlFor="warehouses" className="text-sm cursor-pointer">
                    Warehouses
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="categories"
                    checked={settings.secondaryStats.categories}
                    onCheckedChange={() => toggleSecondaryStat("categories")}
                  />
                  <Label htmlFor="categories" className="text-sm cursor-pointer">
                    Categories
                  </Label>
                </div>
              </div>
            </div>

            <Separator />

            {/* Quick Actions Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-1">Quick Actions</h3>
                <p className="text-xs text-muted-foreground">Select which shortcuts to display</p>
              </div>
              <div className="grid grid-cols-2 gap-3 pl-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="newStockTake"
                    checked={settings.quickActions.newStockTake}
                    onCheckedChange={() => toggleQuickAction("newStockTake")}
                  />
                  <Label htmlFor="newStockTake" className="text-sm cursor-pointer">
                    New Stock Take
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="receiveStock"
                    checked={settings.quickActions.receiveStock}
                    onCheckedChange={() => toggleQuickAction("receiveStock")}
                  />
                  <Label htmlFor="receiveStock" className="text-sm cursor-pointer">
                    Receive Stock
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="transferStock"
                    checked={settings.quickActions.transferStock}
                    onCheckedChange={() => toggleQuickAction("transferStock")}
                  />
                  <Label htmlFor="transferStock" className="text-sm cursor-pointer">
                    Transfer Stock
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="adjustStock"
                    checked={settings.quickActions.adjustStock}
                    onCheckedChange={() => toggleQuickAction("adjustStock")}
                  />
                  <Label htmlFor="adjustStock" className="text-sm cursor-pointer">
                    Adjust Stock
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="viewReports"
                    checked={settings.quickActions.viewReports}
                    onCheckedChange={() => toggleQuickAction("viewReports")}
                  />
                  <Label htmlFor="viewReports" className="text-sm cursor-pointer">
                    View Reports
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="manageLocations"
                    checked={settings.quickActions.manageLocations}
                    onCheckedChange={() => toggleQuickAction("manageLocations")}
                  />
                  <Label htmlFor="manageLocations" className="text-sm cursor-pointer">
                    Manage Locations
                  </Label>
                </div>
              </div>
            </div>

            <Separator />

            {/* Other Sections */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-1">Other Sections</h3>
                <p className="text-xs text-muted-foreground">Additional dashboard components</p>
              </div>
              <div className="grid grid-cols-2 gap-3 pl-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="actionItems"
                    checked={settings.actionItems}
                    onCheckedChange={() => toggleSimpleSetting("actionItems")}
                  />
                  <Label htmlFor="actionItems" className="text-sm cursor-pointer">
                    Action Items
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="stockAlerts"
                    checked={settings.stockAlerts}
                    onCheckedChange={() => toggleSimpleSetting("stockAlerts")}
                  />
                  <Label htmlFor="stockAlerts" className="text-sm cursor-pointer">
                    Stock Alerts
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="recentActivity"
                    checked={settings.recentActivity}
                    onCheckedChange={() => toggleSimpleSetting("recentActivity")}
                  />
                  <Label htmlFor="recentActivity" className="text-sm cursor-pointer">
                    Recent Activity
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="stockByType"
                    checked={settings.stockByType}
                    onCheckedChange={() => toggleSimpleSetting("stockByType")}
                  />
                  <Label htmlFor="stockByType" className="text-sm cursor-pointer">
                    Stock By Type
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="topValueItems"
                    checked={settings.topValueItems}
                    onCheckedChange={() => toggleSimpleSetting("topValueItems")}
                  />
                  <Label htmlFor="topValueItems" className="text-sm cursor-pointer">
                    Top Value Items
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="capabilities"
                    checked={settings.capabilities}
                    onCheckedChange={() => toggleSimpleSetting("capabilities")}
                  />
                  <Label htmlFor="capabilities" className="text-sm cursor-pointer">
                    System Capabilities
                  </Label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex items-center justify-between">
            <Button variant="outline" onClick={resetSettings} className="mr-auto">
              Reset to Default
            </Button>
            <Button onClick={() => setIsSettingsOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contextual Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        helpContent={helpContent}
        onNavigateToPage={(pageId) => navigateTo(pageId)}
      />
    </div>
  );
}
